/**
 * FeedbackService
 * Service for Feedback entity operations
 * Route: api/feedback
 */
import { CrudService } from '../base/CrudService';
/**
 * FeedbackService
 * Handles feedback CRUD operations and feedback-specific queries
 */
export class FeedbackService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FeedbackService.DEFAULT_SERVICE_PATH,
            basePath: FeedbackService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET MY FEEDBACK ====================
    /**
     * Get current user's feedback (excluding anonymous)
     * GET api/feedback/my-feedback
     */
    async getMyFeedback() {
        const response = await this.getMyFeedbackRaw();
        return response.data ?? [];
    }
    /**
     * Get current user's feedback with full response
     */
    getMyFeedbackRaw() {
        const endpoint = this.getEndpoint('my-feedback', 'my-feedback');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET UNRESOLVED ====================
    /**
     * Get all unresolved feedback
     * GET api/feedback/unresolved
     */
    async getUnresolved() {
        const response = await this.getUnresolvedRaw();
        return response.data ?? [];
    }
    /**
     * Get all unresolved feedback with full response
     */
    getUnresolvedRaw() {
        const endpoint = this.getEndpoint('unresolved', 'unresolved');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET BY CATEGORY ====================
    /**
     * Get feedback by category
     * GET api/feedback/category/{categoryId}
     */
    async getByCategory(categoryId) {
        const response = await this.getByCategoryRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get feedback by category with full response
     */
    getByCategoryRaw(categoryId) {
        const endpoint = this.getEndpoint('by-category', `category/${categoryId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== RESOLVE ====================
    /**
     * Resolve feedback
     * PUT api/feedback/{id}/resolve
     */
    async resolve(id) {
        const response = await this.resolveRaw(id);
        if (!response.data) {
            throw new Error(`Failed to resolve feedback ${id}`);
        }
        return response.data;
    }
    /**
     * Resolve feedback with full response
     */
    resolveRaw(id) {
        const endpoint = this.getEndpoint('resolve', `${id}/resolve`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    // ==================== REOPEN ====================
    /**
     * Reopen feedback
     * PUT api/feedback/{id}/reopen
     */
    async reopen(id) {
        const response = await this.reopenRaw(id);
        if (!response.data) {
            throw new Error(`Failed to reopen feedback ${id}`);
        }
        return response.data;
    }
    /**
     * Reopen feedback with full response
     */
    reopenRaw(id) {
        const endpoint = this.getEndpoint('reopen', `${id}/reopen`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    // ==================== MODELVIEW ENDPOINTS ====================
    /**
     * Get feedback ModelView by ID (enriched with type, category, and user data)
     * GET api/feedback/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Feedback ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get feedback ModelView by ID with full response
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all feedback ModelViews (enriched)
     * GET api/feedback/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all feedback ModelViews with full response
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('all-views', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get current user's feedback ModelViews (enriched)
     * GET api/feedback/my-view
     */
    async getMyViews() {
        const response = await this.getMyViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get current user's feedback ModelViews with full response
     */
    getMyViewsRaw() {
        const endpoint = this.getEndpoint('my-views', 'my-view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get unresolved feedback ModelViews (enriched)
     * GET api/feedback/unresolved-view
     */
    async getUnresolvedViews() {
        const response = await this.getUnresolvedViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get unresolved feedback ModelViews with full response
     */
    getUnresolvedViewsRaw() {
        const endpoint = this.getEndpoint('unresolved-views', 'unresolved-view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get feedback ModelViews by category (enriched)
     * GET api/feedback/category/{categoryId}/view
     */
    async getViewsByCategory(categoryId) {
        const response = await this.getViewsByCategoryRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get feedback ModelViews by category with full response
     */
    getViewsByCategoryRaw(categoryId) {
        const endpoint = this.getEndpoint('views-by-category', `category/${categoryId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get feedback ModelViews by type (enriched)
     * GET api/feedback/type/{typeId}/view
     */
    async getViewsByType(typeId) {
        const response = await this.getViewsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get feedback ModelViews by type with full response
     */
    getViewsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('views-by-type', `type/${typeId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get feedback ModelViews by user (enriched)
     * GET api/feedback/user/{userId}/view
     */
    async getViewsByUser(userId) {
        const response = await this.getViewsByUserRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get feedback ModelViews by user with full response
     */
    getViewsByUserRaw(userId) {
        const endpoint = this.getEndpoint('views-by-user', `user/${userId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
FeedbackService.DEFAULT_SERVICE_PATH = 'feedback';
FeedbackService.BASE_PATH = 'api/feedback';
//# sourceMappingURL=FeedbackService.js.map